import SliderInput from '@wandb/weave/common/components/elements/SliderInput';
import {BoundingBoxSliderControl} from '@wandb/weave/common/components/MediaCard';
import {CompareOp} from '@wandb/weave/common/util/ops';
import {Checkbox} from '@wandb/weave/components/Checkbox/Checkbox';
import {Select} from '@wandb/weave/components/Form/Select';
import produce from 'immer';
import {get, set} from 'lodash';
import React, {useState} from 'react';

import {TruncateTextTooltip} from '../../../../../common/TruncateText/TruncateTextTooltip';
import {TruncationType} from '../../../../../common/TruncateText/TruncateTextTypes';
import {useMediaSettingsDrawerContext} from '../../MediaSettingsDrawerContextProvider';

type OperatorOption = {
  text: string;
  value: CompareOp;
  key: string;
};

const OPERATOR_OPTIONS: OperatorOption[] = [
  {text: '≥', value: 'gte', key: 'gte'},
  {text: '≤', value: 'lte', key: 'lte'},
];

export const ConfidenceSlider = ({sliderKey}: {sliderKey: string}) => {
  const {sliderRanges, config, updateConfig} = useMediaSettingsDrawerContext();
  const boundingBoxConfig = config.boundingBoxConfig ?? {};
  const currentMin = sliderRanges?.[sliderKey]
    ? parseFloat(sliderRanges[sliderKey].min.toPrecision(3))
    : null;
  const currentMax = sliderRanges?.[sliderKey]
    ? parseFloat(sliderRanges[sliderKey].max.toPrecision(3))
    : null;
  const currentSliderControl: BoundingBoxSliderControl = get(
    boundingBoxConfig,
    ['sliders', sliderKey],
    {comparator: 'gte', value: currentMin}
  );

  const [selectedComparator, setSelectedComparator] = useState<CompareOp>(
    currentSliderControl.comparator ?? 'gte'
  );

  const updateSlider = (newControl: Partial<BoundingBoxSliderControl>) => {
    if (newControl.comparator) {
      setSelectedComparator(newControl.comparator);
    }
    const newConfig = produce(boundingBoxConfig, draft => {
      set(draft, ['sliders', sliderKey], {
        ...currentSliderControl,
        ...newControl,
      });
    });
    updateConfig({boundingBoxConfig: newConfig});
  };

  const onSliderChange = (v?: number) =>
    v && updateSlider({value: parseFloat(v.toPrecision(3))});

  const onDisableChange = () =>
    updateSlider({disabled: !currentSliderControl.disabled});

  const onOperatorChange = (comparator: CompareOp) =>
    updateSlider({comparator});

  return (
    <div
      className="mt-8 flex h-32 w-full min-w-0 items-center"
      data-test={`bounding-box-confidence-slider-${sliderKey}`}>
      <div
        data-test={`bounding-box-confidence-slider-checkbox-${sliderKey}`}
        className="flex-shrink-0 cursor-pointer rounded p-3 hover:bg-moon-200"
        onClick={onDisableChange}>
        <Checkbox checked={!currentSliderControl.disabled} />
      </div>
      <TruncateTextTooltip
        fullText={sliderKey}
        noLink
        truncationType={TruncationType.Middle}
        textAlign="left"
        className="mx-8 min-w-0 flex-1"
      />
      <Select<OperatorOption>
        className="flex-shrink-0 pr-8"
        aria-label="Comparison operator"
        options={OPERATOR_OPTIONS}
        getOptionLabel={option => option.text}
        value={OPERATOR_OPTIONS.find(
          option => option.value === selectedComparator
        )}
        onChange={newOption => {
          if (newOption) {
            onOperatorChange(newOption.value);
          }
        }}
      />
      {currentMin && currentMax && (
        <SliderInput
          className="flex-shrink-0"
          sliderInPopup
          min={currentMin}
          max={currentMax}
          minLabel={currentMin.toString()}
          maxLabel={currentMax.toString()}
          step={(currentMax - currentMin) / 100}
          strideLength={(currentMax - currentMin) / 100}
          value={currentSliderControl.value}
          hasInput
          onChange={onSliderChange}
          useStepperPlusMinus
        />
      )}
    </div>
  );
};
